import { useCallback, useLayoutEffect, useState } from "react";
import { Outlet, matchPath, useLocation } from "react-router-dom";
import Footer from "../components/Footer";
import useHeaderData from "../hooks/useHeaderData";
import DesktopSidebar from "./DesktopSidebar";
import DesktopTopBar from "./DesktopTopBar";
import { BARE_LEGACY_ROUTES, DESKTOP_NATIVE_ROUTES, WIDE_LEGACY_ROUTES } from "./navItems";
import "./desktop.css";
import "./legacy.css";

const matchesPrefix = (routes, pathname) =>
  routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const SIDEBAR_KEY = "mysolon.ds.sidebarCollapsed";

const readCollapsed = () => {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === "1";
  } catch {
    return false;
  }
};

// Wide-screen app frame: sidebar navigation + top bar + content.
// Desktop-native routes fill the viewport and scroll inside their own panes;
// all other routes render in a centered column like the classic layout.
const DesktopShell = () => {
  const headerData = useHeaderData();
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useLayoutEffect(() => {
    document.documentElement.classList.add("desktop-mode");
    return () => document.documentElement.classList.remove("desktop-mode");
  }, []);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_KEY, next ? "1" : "0");
      } catch {}
      return next;
    });
  }, []);

  const isNative = DESKTOP_NATIVE_ROUTES.some((path) =>
    matchPath({ path, end: true }, pathname)
  );

  // Route class lets legacy.css adapt individual classic pages.
  const legacyClass = [
    "App ds-legacy",
    `ds-route-${pathname.split("/")[1] || "home"}`,
    matchesPrefix(WIDE_LEGACY_ROUTES, pathname) && "ds-legacy--wide",
    matchesPrefix(BARE_LEGACY_ROUTES, pathname) && "ds-legacy--bare",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={`ds-shell ${collapsed ? "is-collapsed" : ""}`}>
      <DesktopSidebar
        pathname={pathname}
        collapsed={collapsed}
        onToggleCollapsed={toggleCollapsed}
        onSignOut={headerData.signOut}
      />
      <div className="ds-body">
        <DesktopTopBar headerData={headerData} pathname={pathname} />
        {isNative ? (
          <div className="ds-native">
            <Outlet />
          </div>
        ) : (
          <>
            <main className={legacyClass}>
              <Outlet />
            </main>
            <Footer />
          </>
        )}
      </div>
    </div>
  );
};

export default DesktopShell;
